// MobileMenu.js
// Premium Avayble mobile nav: a copper "Menu" peek bar + a bottom-sheet drawer.
// Stack: React + react-router-dom + lucide-react + the existing Atelier tokens.
//
// Usage (in your mobile layout, rendered once):
//   import MobileMenu, { MenuPeekBar } from './MobileMenu';
//   const [open, setOpen] = useState(false);
//   <MenuPeekBar onClick={() => setOpen(true)} />
//   <MobileMenu open={open} onClose={() => setOpen(false)} user={currentUser} />
//
// `user` is optional: { email, name, businessName }. Active item is derived
// from the current route — no prop needed. Routes below match src/App.js.

import React, { useContext, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, Users, Clock, CalendarRange, Briefcase,
  Sparkles, Package, MapPin, Megaphone, Receipt, Settings, LogOut, X,
  Inbox, Home as HomeIcon, Menu as MenuIcon, Globe,
  Repeat, Gift as GiftIcon, Tag, CreditCard, ClipboardList, MessageSquare, BadgePercent,
} from 'lucide-react';
import { AuthContext } from '../../AuthContext';
import useAssistantAvailable from '../../assistant/useAssistantAvailable';
import usePriceBoardEnabled from '../../prices/usePriceBoardEnabled';

// Swipe-gesture threshold tuned to feel like iOS sheet handles:
// either a 30px drag or a fast flick (velocity > 0.4 px/ms) counts.
// Tap still works — touchend without crossing the threshold falls
// through to the click handler. `direction` is 'up' (for peek-to-open)
// or 'down' (for sheet-to-close).
function useSwipeGesture(direction, onTrigger) {
  const state = useRef({ startY: 0, startT: 0, triggered: false });
  const onTouchStart = (e) => {
    state.current = {
      startY: e.touches[0].clientY,
      startT: Date.now(),
      triggered: false,
    };
  };
  const onTouchMove = (e) => {
    if (state.current.triggered) return;
    const dy = e.touches[0].clientY - state.current.startY;
    const dt = Math.max(1, Date.now() - state.current.startT);
    // Signed delta: dy < 0 = upward, dy > 0 = downward.
    const signed = direction === 'up' ? -dy : dy;
    const velocity = signed / dt; // px/ms in the intended direction
    if (signed > 30 || velocity > 0.4) {
      state.current.triggered = true;
      onTrigger();
    }
  };
  return { onTouchStart, onTouchMove };
}

// PROVIDER nav — the studio-owner side of the app.
const PROVIDER_GROUPS = [
  { label: '', items: [
    { id: 'dashboard',    label: 'Dashboard',    to: '/provider',              Icon: LayoutDashboard },
    { id: 'appointments', label: 'Appointments', to: '/provider/appointments', Icon: Calendar },
    { id: 'requests',     label: 'Requests',     to: '/provider/requests',     Icon: Inbox },
    { id: 'roster',       label: 'Roster',       to: '/provider/roster',       Icon: Users },
    { id: 'notes',        label: 'SOAP notes',   to: '/provider/notes',        Icon: ClipboardList },
    // Shown only when the assistant is on for this practice (useAssistantAvailable).
    { id: 'assistant',    label: 'Assistant',    to: '/provider/assistant',    Icon: MessageSquare },
  ]},
  { label: 'Schedule', items: [
    { id: 'availability', label: 'Availability', to: '/provider/availability',       Icon: Clock },
    { id: 'weekly-hours', label: 'Weekly hours', to: '/provider/schedule-template',  Icon: CalendarRange },
    { id: 'events',       label: 'Events',       to: '/provider/events',             Icon: Briefcase },
  ]},
  { label: 'Catalog', items: [
    { id: 'services',  label: 'Services',  to: '/provider/services',  Icon: Sparkles },
    // The price board replaces Services where it's on (usePriceBoardEnabled).
    { id: 'prices',    label: 'Prices',    to: '/provider/prices',    Icon: Tag },
    { id: 'packages',  label: 'Packages',  to: '/provider/packages',  Icon: Package },
    { id: 'memberships', label: 'Memberships', to: '/provider/memberships', Icon: Repeat },
    { id: 'gifts',     label: 'Gifts',     to: '/provider/gifts',     Icon: GiftIcon },
    { id: 'locations', label: 'Locations', to: '/provider/locations', Icon: MapPin },
  ]},
  { label: 'Business', items: [
    { id: 'outreach', label: 'Outreach', to: '/provider/outreach', Icon: Megaphone },
    { id: 'promotions', label: 'Promotions', to: '/provider/promotions', Icon: BadgePercent },
    { id: 'reports',  label: 'Reports',  to: '/provider/reports',  Icon: Receipt },
    { id: 'billing',  label: 'Billing',  to: '/provider/billing',  Icon: CreditCard },
  ]},
];

// CLIENT nav — what someone booking with a provider sees. The Avayble
// app has two distinct personas behind the same login surface; before
// this file branched on accountType, clients were seeing the full
// provider menu (Dashboard / Roster / Outreach / Reports — none of
// which they have access to). Routes match the canonical client paths
// in src/App.js — /book, /my-bookings, /my-packages, etc.
// CLIENT_GROUPS removed 2026-08-27 — clients use ClientMenuSheet (the
// one unified menu); this component now serves providers only.

const eyebrow = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.18em',
  textTransform: 'uppercase', color: 'var(--ink-3)',
};

// Pages that own the bottom of the screen, where the dock steps aside
// (2026-10-25): the Assistant's chat box sat under it ("the chat text
// entry is obscured by the floating menu"). Each brings its own way back.
export const OWNS_BOTTOM = ['/provider/assistant'];

// Floating dock (Ivan's pick A, 2026-09-03) — replaces the full-width
// copper "Menu" flood. Four PRECISE primary destinations (exact labels,
// exact routes) plus More, which opens the existing drawer. Requests
// came off the dock 2026-09-04 (infrequent, and already surfaced by
// the notification bell) in favor of Availability. Requests stays in
// the drawer; Weekly hours, Catalog, Business keep their names there.
export function MenuPeekBar({ onClick }) {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const isProvider = user?.accountType === 'PROVIDER';
  const swipe = useSwipeGesture('up', onClick);
  if (OWNS_BOTTOM.includes(pathname.replace(/\/+$/, ''))) return null;

  // Clients' labels are a size up (2026-10-25, the client home's "tiny
  // text"); the provider's five-item dock keeps 9.5px to fit.
  const items = isProvider
    ? [
        { label: 'Home', to: '/provider', Icon: HomeIcon, exact: true },
        { label: 'Appointments', to: '/provider/appointments', Icon: Calendar },
        { label: 'Availability', to: '/provider/availability', Icon: CalendarRange },
        { label: 'Roster', to: '/provider/roster', Icon: Users },
      ]
    : [
        { label: 'Home', to: '/', Icon: HomeIcon, exact: true },
        { label: 'Bookings', to: '/my-bookings', Icon: Calendar },
        { label: 'Packages', to: '/my-packages', Icon: Package },
      ];

  // A client's home is their provider's page (/ routes there), so Home
  // lights up on /p/:joinCode too — not on its /book or /gift.
  const isActive = (item) => {
    if (!isProvider && item.to === '/' && /^\/p\/[^/]+\/?$/.test(pathname)) return true;
    return item.exact ? pathname === item.to : pathname.startsWith(item.to);
  };

  return (
    <div
      /* display lives in the class list, NOT inline style — an inline
         display:flex beats the lg:hidden media rule and floats this
         dock over every desktop page (found 2026-09-05). */
      className="lg:hidden fixed z-30 flex justify-between items-center"
      onTouchStart={swipe.onTouchStart}
      onTouchMove={swipe.onTouchMove}
      style={{
        left: 14, right: 14,
        bottom: 'max(12px, env(safe-area-inset-bottom))',
        background: 'rgba(251,247,239,0.97)',
        border: '1px solid rgba(42,37,32,0.10)',
        borderRadius: 24,
        boxShadow: '0 12px 32px rgba(42,37,32,0.16), 0 2px 6px rgba(42,37,32,0.06)',
        padding: '8px 16px 7px',
      }}
    >
      {items.map((item) => {
        const active = isActive(item);
        const Icon = item.Icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            className="flex flex-col items-center"
            style={{ gap: 3, position: 'relative', color: active ? 'var(--accent-ink)' : 'var(--ink-3)', textDecoration: 'none', minWidth: 52 }}
          >
            <span
              style={active
                ? { width: 34, height: 24, borderRadius: 999, background: 'var(--accent-soft)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }
                : { display: 'inline-flex', height: 24, alignItems: 'center' }}
            >
              <Icon style={{ width: 18, height: 18 }} strokeWidth={1.7} />
            </span>
            <span style={{ fontSize: isProvider ? 9.5 : 11, fontWeight: active ? 600 : 400 }}>{item.label}</span>
          </Link>
        );
      })}
      <button
        type="button"
        onClick={onClick}
        aria-label="Open menu"
        className="flex flex-col items-center"
        style={{ gap: 3, background: 'none', border: 0, cursor: 'pointer', color: 'var(--ink-3)', padding: 0, minWidth: 52, font: 'inherit' }}
      >
        <span style={{ display: 'inline-flex', height: 24, alignItems: 'center' }}>
          <MenuIcon style={{ width: 18, height: 18 }} strokeWidth={1.7} />
        </span>
        <span style={{ fontSize: isProvider ? 9.5 : 11 }}>More</span>
      </button>
    </div>
  );
}

function NavRow({ item, active, onClick }) {
  const { Icon, label, to } = item;
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-1.5 rounded-xl transition-colors ${active ? '' : 'hover:bg-paper-deep'}`}
      style={active ? { background: 'var(--accent-soft)' } : undefined}
    >
      <span
        className={`w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 ${active ? 'text-white' : 'text-ink-2 bg-paper-deep'}`}
        style={active ? { background: 'var(--accent)', boxShadow: 'var(--shadow-sm)' } : undefined}
      >
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <span className={`text-[15px] whitespace-nowrap ${active ? 'text-accent-ink font-medium' : 'text-ink'}`}>{label}</span>
      {active && <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: 'var(--accent)' }} />}
    </Link>
  );
}

export default function MobileMenu({ open, onClose, user, onLogout }) {
  const { pathname } = useLocation();
  // PROVIDER-ONLY as of 2026-08-27: clients get the ONE unified menu
  // (ClientMenuSheet) — Header branches before rendering this. The
  // isClient identity styling below is retained defensively for any
  // legacy caller, but the nav list is always the provider's.
  const isClient = user?.accountType === 'CLIENT';
  // "My public page" → the provider's own landing page, which doubles
  // as its editor (Ivan, 2026-09-04). Injected at render because the
  // path carries the provider's joinCode.
  const assistantOn = useAssistantAvailable(user);
  const pricesOn = usePriceBoardEnabled(user) === true;
  const shownGroups = PROVIDER_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => (it.id !== 'assistant' || assistantOn)
      && (it.id !== 'prices' || pricesOn) && (it.id !== 'services' || !pricesOn)),
  }));
  const groups = user?.joinCode
    ? shownGroups.map((g, i) => (i === 0
        ? { ...g, items: [...g.items, { id: 'my-page', label: 'My public page', to: `/p/${user.joinCode}`, Icon: Globe }] }
        : g))
    : shownGroups;

  // Identity bits.
  //   - Providers: brand-forward — logo from providerProfile.logoUrl,
  //     eyebrow "Provider · {businessName}".
  //   - Clients:   person-forward — their initial (no logo concept on the
  //     client side), eyebrow "Client".
  const logoUrl = !isClient ? (user?.providerProfile?.logoUrl || null) : null;
  const displayName = isClient
    ? (user?.profile?.fullName || user?.email || 'Your account')
    : (user?.providerProfile?.businessName || 'Avayble Provider');
  const eyebrowText = isClient ? 'Client' : `Provider · ${displayName}`;
  const settingsTo = isClient ? '/my-profile' : '/provider/settings';

  // Swipe-down-to-close, anchored on the drag-handle + identity header.
  // We don't attach the swipe to the whole sheet body because the
  // scrollable nav area needs to keep its native scroll-by-touch behavior.
  const closeSwipe = useSwipeGesture('down', onClose);

  // Exact match for "dashboard-ish" roots ("/provider", "/book") so every
  // sub-route doesn't light them up; prefix match for everything else.
  // /book is exact-only because /book/fork is the picker, not the form.
  const isActive = (to) => {
    if (to === '/provider' || to === '/book') return pathname === to;
    return pathname === to || pathname.startsWith(to + '/');
  };

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <div className="lg:hidden" aria-hidden={!open}>
      {/* Scrim */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-40"
        style={{
          background: 'rgba(42,37,32,0.5)',
          backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
          opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity .25s ease',
        }}
      />
      {/* Sheet */}
      <div
        role="dialog" aria-modal="true"
        className="fixed bottom-0 inset-x-0 z-50 flex flex-col bg-paper-elev"
        style={{
          maxHeight: '88vh',
          borderRadius: '26px 26px 0 0',
          boxShadow: 'var(--shadow-lg)',
          borderTop: '1px solid rgba(255,255,255,0.5)',
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform .34s cubic-bezier(0.32,0.72,0,1)',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {/* Drag handle — swipe down here (or on the identity header below)
            to close. Touch handlers cover this whole top region. */}
        <div
          className="pt-2 pb-0.5 flex justify-center flex-shrink-0 touch-pan-x"
          onTouchStart={closeSwipe.onTouchStart}
          onTouchMove={closeSwipe.onTouchMove}
        >
          <span className="block w-10 h-1 rounded-full" style={{ background: 'var(--ink-3)', opacity: 0.35 }} />
        </div>

        {/* Identity header — pulls the provider's uploaded logo from
            user.providerProfile.logoUrl (set via Settings > LogoUploader)
            and falls back to a copper-soft initial chip when no logo is set.
            Swipe-down-to-close also lives on this header so the gesture is
            forgiving — you don't have to land exactly on the drag handle. */}
        <div
          className="px-5 pt-1.5 pb-3 flex items-center gap-3 border-b border-line flex-shrink-0 touch-pan-x"
          onTouchStart={closeSwipe.onTouchStart}
          onTouchMove={closeSwipe.onTouchMove}
        >
          {logoUrl ? (
            <span
              className="w-11 h-11 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center"
              style={{ background: 'var(--bg-deep)' }}
            >
              <img
                src={logoUrl}
                alt={`${displayName} logo`}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </span>
          ) : (
            <span
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 font-display font-semibold"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent-ink)', fontSize: 18 }}
            >
              {(user?.profile?.fullName || user?.name || user?.email || 'I').charAt(0).toUpperCase()}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-display text-ink truncate" style={{ fontSize: '1.0625rem', fontWeight: 500, lineHeight: 1.2 }}>
              {user?.email || 'Your account'}
            </div>
            <div className="mt-0.5" style={eyebrow}>{eyebrowText}</div>
          </div>
          <button onClick={onClose} aria-label="Close menu"
                  className="w-10 h-10 -mr-1 flex items-center justify-center rounded-full text-ink-2 hover:bg-paper-deep flex-shrink-0">
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>

        {/* Scrollable nav — tightened vertical rhythm so the whole tree
            fits in one view on a 700px-tall phone without scrolling. */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-2.5 pt-0.5 pb-4" style={{ overscrollBehavior: 'contain' }}>
          {groups.map((g, i) => (
            <div key={i}>
              {g.label && <div className="px-3 pt-3 pb-1" style={eyebrow}>{g.label}</div>}
              <div className="space-y-0.5">
                {g.items.map((item) => (
                  <NavRow key={item.id} item={item} active={isActive(item.to)} onClick={onClose} />
                ))}
              </div>
            </div>
          ))}

          {/* Account.
              Providers get a dedicated Settings link to /provider/settings
              (the studio config dashboard). Clients don't — their
              configurable surfaces (Profile, Intake, Preferences) are
              already in the main list above. */}
          <div className="px-3 pt-3 pb-1" style={eyebrow}>Account</div>
          {!isClient && (
            <Link to={settingsTo} onClick={onClose}
                  className="flex items-center gap-3 px-3 py-1.5 rounded-xl hover:bg-paper-deep transition-colors">
              <span className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 text-ink-2 bg-paper-deep">
                <Settings size={16} strokeWidth={1.75} />
              </span>
              <span className="text-[15px] text-ink">Settings</span>
            </Link>
          )}
          <button onClick={() => { onClose(); onLogout && onLogout(); }}
                  className="w-full flex items-center gap-3 px-3 py-1.5 rounded-xl transition-colors"
                  style={{ color: 'var(--r-off-ink)' }}
                  onMouseOver={(e) => (e.currentTarget.style.background = 'var(--r-off-soft)')}
                  onMouseOut={(e) => (e.currentTarget.style.background = '')}>
            <span className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--r-off-soft)', color: 'var(--r-off-ink)' }}>
              <LogOut size={16} strokeWidth={1.75} />
            </span>
            <span className="text-[15px] font-medium">Logout</span>
          </button>

          <div className="px-3 pt-3 pb-1 flex items-center justify-between" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            <span>Avayble</span>
            {/* Privacy lives as a static HTML page in /public, so this is a plain
                anchor — using react-router <Link> would route into a 404. */}
            <a href="/privacy-policy.html" onClick={onClose}>Privacy</a>
          </div>
        </div>
      </div>
    </div>
  );
}
